// Generates the school year's assignment docs from the real "Cedar Vale
// Sixth Grade Year Plan" — two structurally different kinds of output:
//
//   1. The "daily six" — Math/Literature/History+Geography/Typing/Spelling/
//      Devotion, isFloor: true, placementMode: 'fixed' (a specific date,
//      same as before), one full day-by-day generation like the old seed.
//   2. The "weekly board" — 8 tiles the STUDENT distributes across the
//      week (Composition, Cursive, Digital Savvy, Science-text, Science-
//      lab, Mechanics, Critical & Creative, Discovery). These are created
//      with weekNumber set and date/dayOfWeek/order left null —
//      placementMode: 'student-placed' — and only get a date once the
//      student places them on the week board (WeekBoard.jsx).
//
// Math/History pacing below is grounded in her explicit week-by-week table
// through week 29 (Apr 5); weeks 30-37 (Dimensions 6A's back half) only had
// a coarse "twelve weeks, roughly chapters 1-5" milestone in the source, so
// those weeks use a generic "6A continuing" label rather than invented
// chapter numbers — replace once she gives the finer pacing.
import { addDaysIso, isoWeekdayNumber } from '../calendarMath';
import { ANCHORS, HOLIDAYS, WEEKS } from './calendar';

// ─── DAILY SIX PACING ─────────────────────────────────────────────────────────

// One entry per week (WEEKS[i]), from her "Week calendar" table.
const MATH_PACING = [
  '5A Ch4 L1-3', '5A Ch4 L4-7', '5A Ch4 L8-9 + test', '5A Ch5 L1-4', '5A Ch5 L5-8',
  '5A Ch5 L9-11 + Review 1', '5A Ch6 L1-4', '5A Ch6 L5-7 + test', '5A Ch7 L1-4', '5A Ch7 L5-7 + Ch8 L1',
  '5A Ch8 L2-3', '5A Ch8 L4-7', '5A Ch8 L8 + Review 2 + test', '5B Ch9 — decimals', '5B Ch9',
  '5B Ch9 complete', '5B Ch10 — decimal ops', '5B Ch10', '5B Ch10 complete', '5B Ch13 — ratio',
  '5B Ch13', '5B Ch14 — rate', '5B Ch15 — percentage', '5B Ch15', '5B Ch15 + test',
  '6A opens — Victorian era', '6A Ch1', '6A Ch1', '6A Ch2', '6A continuing',
  '6A continuing', '6A continuing', '6A continuing', '6A continuing', '6A continuing',
  '6A continuing', '6A continuing',
];

// History "unit in progress" per week — weeks 29-37 hand off to the spring
// unit (see SPRING_UNIT below), so this array only needs to cover weeks 1-28.
const HISTORY_UNIT_LABEL = [
  'Unit 1', 'Unit 2', 'Unit 3', 'Unit 4', 'Unit 5', 'Unit 6', 'Unit 7 — Greece', 'Unit 8 — Rome',
  'Unit 9', 'Unit 10', 'Unit 11', 'Unit 11-12', 'Unit 13', 'Unit 14', 'Unit 15', 'Unit 16',
  'Unit 17', 'Unit 18', 'Unit 19', 'Unit 20', 'Unit 21', 'Unit 22', 'Unit 23', 'Unit 24 — revolutions',
  'Unit 25', 'Unit 26 — Victorian', 'Unit 26 cont.', 'Unit 27 — WWI',
];

// Five weeks (28-37, but literature/discovery only fully hand off starting
// 29) where literature, history, and the Friday discovery block run as one
// interdisciplinary unit — replaces the normal history/literature pacing.
const SPRING_UNIT = [
  { weeks: [28], label: 'Spring unit groundwork — Notgrass Unit 27, WWI' },
  { weeks: [29, 30], label: 'A Family Secret — Notgrass Unit 28, WWII' },
  { weeks: [30, 31], label: 'The Search — pairs with Lesson 137, Berlin & Cold War' },
  { weeks: [31, 32], label: 'Stealing Home — Japanese Canadian internment' },
  { weeks: [33, 34, 35, 36, 37], label: 'Write and draw your own comic — replaces the written history final' },
];

function springUnitLabelForWeek(weekNumber) {
  for (const s of SPRING_UNIT) if (s.weeks.includes(weekNumber)) return s.label;
  return null;
}

// Mosdos Pearl (6 units) carries the first half of the year; general
// free-choice reading fills the middle; the spring unit's graphic novels
// take over literature for weeks 29+. Coarser than the math/history tables
// because that's the level of detail the source document gives for
// literature specifically.
function literatureText(weekNumber) {
  if (weekNumber >= 29) return null; // spring unit covers it — see buildDailySix
  if (weekNumber <= 16) return `Mosdos Pearl, Unit ${Math.min(6, Math.ceil(weekNumber / 3))}`;
  return 'Free-choice reading (Learning Ally / Seeing AI)';
}

// ─── DAILY SIX ────────────────────────────────────────────────────────────────

function buildDailySix(dow, weekNumber, date) {
  const items = [];
  const idx = weekNumber - 1;

  items.push({
    itemId: 'math', block: 'math', courseId: 'math', title: 'Math',
    subLabel: MATH_PACING[idx] || '6A continuing', estMinutes: 40, isFloor: true, deliveryMode: 'person',
  });

  const spring = springUnitLabelForWeek(weekNumber);
  items.push({
    itemId: 'literature', block: 'la', courseId: 'literature', title: 'Literature',
    subLabel: spring || literatureText(weekNumber) || undefined, estMinutes: 35, isFloor: true,
  });

  items.push({
    itemId: 'history', block: 'history', courseId: 'history', title: 'History',
    subLabel: spring || HISTORY_UNIT_LABEL[idx] || 'Spring unit',
    estMinutes: dow === 5 ? 40 : 23, // Friday carries the double lesson
    isFloor: true, deliveryMode: 'independent',
  });
  items.push({
    itemId: 'geography', block: 'history', courseId: 'geography', title: 'Daily Geography',
    subLabel: 'Two questions off this week\'s map', estMinutes: 7, isFloor: true,
  });

  items.push({
    itemId: 'typing', block: 'launch', courseId: 'typing', title: 'TTRS typing',
    estMinutes: 15, isFloor: true, deliveryMode: 'independent',
  });
  items.push({
    itemId: 'spelling', block: 'la', courseId: 'spelling', title: 'Spelling You See G',
    estMinutes: 10, isFloor: true, deliveryMode: 'person', // dictation, ten-minute hard stop
  });
  items.push({
    itemId: 'devotion', block: 'launch', courseId: 'devotion', title: 'Indescribable devotion',
    estMinutes: 5, isFloor: true,
  });

  // Tue/Thu launch add-on, fixed (not a weekly-board tile) — Mindfulness
  // until Fallacy Detective takes over at week 16.
  if (dow === 2 || dow === 4) {
    const useFallacy = weekNumber >= 16;
    items.push({
      itemId: useFallacy ? 'fallacy-detective' : 'mindfulness',
      block: 'launch',
      courseId: useFallacy ? 'fallacy-detective' : 'mindfulness',
      title: useFallacy ? 'The Fallacy Detective' : 'Mindfulness for Kids',
      estMinutes: 10, isFloor: false,
      deliveryMode: useFallacy ? 'person' : 'independent', // "the arguing is the course"
    });
  }

  return items;
}

function blank(base, nowIso, source, extra = {}) {
  return {
    needsParent: false,
    needsReview: true,
    ...base,
    confusionFlag: false,
    status: 'not_started',
    reviewedByParent: false,
    source,
    createdAt: nowIso,
    updatedAt: nowIso,
    ...extra,
  };
}

// ─── WEEKLY BOARD ─────────────────────────────────────────────────────────────
// Her "his to distribute" table (Cedar-Vale-6th-Grade-Planner-2026-2027,
// "The Weekly Board" — 9 tiles, 17 sessions, ~400 min) — perWeek sessions of
// each tile, no date assigned here. The student places each session onto a
// day on the week board (see api.js's placeAssignment). itemId
// disambiguates multiple tiles sharing one courseId (science-text vs
// science-lab). Dr. Bonyfide and Geography/Turing Tumble are two distinct
// tiles in her table, not one merged "Discovery" tile as originally built.
const WEEKLY_BOARD_TILES = [
  { itemId: 'composition', courseId: 'composition', block: 'la', title: 'Composition',
    subLabel: 'Wordsmith Apprentice, then Jump In', perWeek: 3, estMinutes: 20,
    needsParent: true, deliveryMode: 'tool' }, // scribed today, dictation is the term-3 target
  { itemId: 'cursive', courseId: 'cursive', block: 'la', title: 'Cursive copywork',
    subLabel: 'Half a page', perWeek: 4, estMinutes: 15,
    needsParent: false, deliveryMode: 'independent' },
  { itemId: 'digital-savvy', courseId: 'digital-savvy', block: 'afternoon', title: 'Digital Savvy',
    subLabel: 'Two of three land after swimming', perWeek: 3, estMinutes: 30,
    needsParent: false, deliveryMode: 'independent' },
  { itemId: 'science-text', courseId: 'science', block: 'afternoon', title: 'Science — text',
    subLabel: 'Real Science-4-Kids Book 6 — read aloud / Immersive Reader', perWeek: 1, estMinutes: 45,
    needsParent: true, deliveryMode: 'tool' },
  { itemId: 'science-lab', courseId: 'science', block: 'afternoon', title: 'Science — lab',
    subLabel: 'Laboratory notebook — pick a day ahead so a parent can set up', perWeek: 1, estMinutes: 45,
    needsParent: true, deliveryMode: 'person' },
  { itemId: 'mechanics', courseId: 'mechanics', block: 'la', title: 'Mechanics — oral',
    subLabel: 'A parent scribes — not Mon/Wed, the tutor has those', perWeek: 2, estMinutes: 10,
    needsParent: true, deliveryMode: 'person', avoidDays: [1, 3] },
  { itemId: 'critical-creative', courseId: 'critical-creative', block: 'logic', title: 'Critical & Creative Thinking',
    subLabel: 'A unit may span two weeks', perWeek: 1, estMinutes: 30,
    needsParent: false, deliveryMode: 'independent' },
  { itemId: 'bonyfide', courseId: 'bonyfide', block: 'afternoon', title: 'Dr. Bonyfide',
    subLabel: 'Friday default', perWeek: 1, estMinutes: 25,
    needsParent: false, deliveryMode: 'independent' },
  // Alternates courseId week to week between geography and Turing Tumble —
  // see buildWeeklyBoardAssignments below.
  { itemId: 'geo-turing', courseId: null, block: 'afternoon', title: null,
    subLabel: 'Alternating · Friday default', perWeek: 1, estMinutes: 25,
    needsParent: false, deliveryMode: 'independent' },
];

const GEO_TURING_ALTERNATES = [
  { courseId: 'skill-sharpeners-geography', title: 'Geography' },
  { courseId: 'turing-tumble', title: 'Turing Tumble' },
];

function buildWeeklyBoardAssignments(studentEmail, weekNumber, nowIso) {
  const out = [];
  for (const tile of WEEKLY_BOARD_TILES) {
    const alt = tile.itemId === 'geo-turing' ? GEO_TURING_ALTERNATES[weekNumber % 2] : null;
    for (let n = 1; n <= tile.perWeek; n++) {
      out.push(
        blank(
          {
            id: `series__${weekNumber}__${tile.itemId}__${n}`,
            studentEmail, courseId: alt ? alt.courseId : tile.courseId,
            date: null, weekNumber, dayOfWeek: null, order: null,
            block: tile.block, title: alt ? alt.title : tile.title, subLabel: tile.subLabel,
            estMinutes: tile.estMinutes, isFloor: false,
            placementMode: 'student-placed',
            needsParent: tile.needsParent, deliveryMode: tile.deliveryMode,
            avoidDays: tile.avoidDays,
          },
          nowIso,
          'seed'
        )
      );
    }
  }
  return out;
}

// ─── FULL GENERATOR ───────────────────────────────────────────────────────────

export function generateYearPlanAssignments(studentEmail, nowIso = new Date().toISOString()) {
  const out = [];

  WEEKS.forEach((monday, weekIdx) => {
    const weekNumber = weekIdx + 1;

    for (let dow = 1; dow <= 5; dow++) {
      const date = addDaysIso(monday, dow - 1);
      if (HOLIDAYS[date]) continue;
      if (isoWeekdayNumber(date) !== dow) continue; // defensive: WEEKS entries should always be Mondays

      buildDailySix(dow, weekNumber, date).forEach((item, order) => {
        out.push(
          blank(
            {
              id: `${date}__${item.block}__${item.itemId}`,
              studentEmail, courseId: item.courseId,
              date, weekNumber, dayOfWeek: dow, order,
              block: item.block, title: item.title, subLabel: item.subLabel,
              estMinutes: item.estMinutes, isFloor: item.isFloor,
              placementMode: 'fixed',
            },
            nowIso,
            'seed',
            { deliveryMode: item.deliveryMode }
          )
        );
      });

      for (const [anchorId, cfg] of Object.entries(ANCHORS)) {
        if (!cfg.days.includes(dow)) continue;
        out.push(
          blank(
            {
              id: `${date}__fixed__${anchorId}`,
              studentEmail, courseId: anchorId,
              date, weekNumber, dayOfWeek: dow, order: 900,
              block: 'fixed', title: cfg.label, estMinutes: cfg.mins, isFloor: false,
              placementMode: 'fixed',
            },
            nowIso,
            'seed'
          )
        );
      }
    }

    out.push(...buildWeeklyBoardAssignments(studentEmail, weekNumber, nowIso));
  });

  return out;
}
